import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Modal, ModalBody, Spinner } from 'reactstrap';
import axios from 'axios';
import api from '../../config.js';
import BreadCrumb, { partnerWithUsBg } from '../components/BreadCrumb.jsx';

const AdvisoryCouncilPage = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMember, setSelectedMember] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const res = await axios.get('/api/public/advisory-council');
                if (res.data.isOk) {
                    setMembers(res.data.data || []);
                }
            } catch (err) {
                console.error('Error fetching advisory council members:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchMembers();
    }, []);

    const openModal = (member) => {
        setSelectedMember(member);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedMember(null);
    };

    const getPhotoUrl = (photo) => {
        if (!photo) return null;
        return `${api.API_URL}/${photo}`;
    };

    return (
        <>
            <BreadCrumb
                backgroundImage={partnerWithUsBg}
                title={"Advisory Council"}
                description={"Meet the distinguished leaders guiding our vision and strategy."}
            />

            <section className="advisory-council-section">
                <Container>
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                            <Spinner color="danger" />
                        </div>
                    ) : members.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-muted">No advisory council members found.</p>
                        </div>
                    ) : (
                        <Row>
                            {members.map((member) => (
                                <Col key={member._id} xs="12" sm="6" md="4" lg="3" className="mb-4">
                                    <div
                                        className="advisory-card"
                                        onClick={() => openModal(member)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && openModal(member)}
                                    >
                                        <div className="advisory-card-photo-wrap">
                                            {getPhotoUrl(member.photo) ? (
                                                <img
                                                    src={getPhotoUrl(member.photo)}
                                                    alt={member.name}
                                                    className="advisory-card-photo"
                                                />
                                            ) : (
                                                <div className="advisory-card-photo-placeholder">
                                                    <i className="fas fa-user"></i>
                                                </div>
                                            )}
                                        </div>
                                        <div className="advisory-card-body">
                                            <h5 className="advisory-card-name">{member.name}</h5>
                                            <p className="advisory-card-designation">{member.designation}</p>
                                            <p className="advisory-card-org">{member.organization}</p>
                                            <div className="d-flex align-items-center gap-2 mt-auto">
                                                <span className="advisory-read-bio">
                                                    <i className="fas fa-info-circle me-1"></i>Read bio
                                                </span>
                                                {member.linkedinUrl && (
                                                    <a
                                                        href={member.linkedinUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="advisory-card-linkedin"
                                                        aria-label="LinkedIn profile"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <i className="fab fa-linkedin"></i>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    )}
                </Container>
            </section>

            <Modal isOpen={modalOpen} toggle={closeModal} centered size="md" className="advisory-modal">
                {selectedMember && (
                    <ModalBody className="advisory-modal-body">
                        <button className="advisory-modal-close" onClick={closeModal} aria-label="Close">
                            &times;
                        </button>
                        <div className="advisory-modal-photo-wrap">
                            {getPhotoUrl(selectedMember.photo) ? (
                                <img
                                    src={getPhotoUrl(selectedMember.photo)}
                                    alt={selectedMember.name}
                                    className="advisory-modal-photo"
                                />
                            ) : (
                                <div className="advisory-modal-photo-placeholder">
                                    <i className="fas fa-user"></i>
                                </div>
                            )}
                        </div>
                        <h4 className="advisory-modal-name">{selectedMember.name}</h4>
                        <p className="advisory-modal-org">{selectedMember.organization}</p>
                        <p className="advisory-modal-designation">{selectedMember.designation}</p>
                        {selectedMember.bio && (
                            <p className="advisory-modal-bio">{selectedMember.bio}</p>
                        )}
                        {selectedMember.linkedinUrl && (
                            <a
                                href={selectedMember.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="advisory-modal-linkedin"
                                aria-label="LinkedIn profile"
                            >
                                <i className="fab fa-linkedin"></i>
                            </a>
                        )}
                    </ModalBody>
                )}
            </Modal>
        </>
    );
};

export default AdvisoryCouncilPage;
